import type { AssetClass } from '@garnet/domain';

/**
 * Local mirror of `@garnet/config`'s `VerticalConfig` (Garnet PRD §38). The published
 * package is not wired into this app's dependency set yet (deps are react +
 * @garnet/ui + @garnet/domain only, and we may not run install), so the vertical copy
 * lives here with an identical shape. Swapping `import type { VerticalConfig } from
 * '@garnet/config'` and `LAUNCH_VERTICALS` in later is a one-line change — no screen
 * string is hard-coded against a vertical (§38.1).
 */
export interface VerticalConfig {
  vertical: AssetClass;
  display_name: string;
  identity: {
    identifier_label: string;
    identifier_kinds: string[];
    carrier: 'gs1_datamatrix' | 'gs1_128' | 'qr' | 'none';
    parse_hint: string;
  };
  upload: {
    document_labels: string[];
    baseline_documents: string[];
  };
  decision_wording: {
    qualify: string;
    qualify_with_conditions: string;
    block: string;
  };
  portal_copy: {
    invitation_heading: string;
    submit_heading: string;
  };
}

export const MEDICAL: VerticalConfig = {
  vertical: 'medical',
  display_name: 'Medical equipment',
  identity: {
    identifier_label: 'UDI-DI',
    identifier_kinds: ['basic_udi_di', 'udi_di', 'gtin'],
    carrier: 'gs1_datamatrix',
    parse_hint: 'Scan or paste the full UDI carrier string; DI and PI parse live.',
  },
  upload: {
    document_labels: [
      'CE certificate (MDR)',
      'ISO 13485 certificate',
      'Declaration of conformity',
      'Free-sale certificate',
    ],
    baseline_documents: ['business_registration', 'ce_mdr_cert', 'declaration_of_conformity', 'insurance_certificate'],
  },
  decision_wording: {
    qualify: 'Qualify supplier',
    qualify_with_conditions: 'Qualify with conditions',
    block: 'Block supplier',
  },
  portal_copy: {
    invitation_heading: 'You have been asked to verify a medical-device supply relationship',
    submit_heading: 'Submit your device and certificate details',
  },
};

/** The active vertical for this portal session (a real deployment selects per deal). */
export const ACTIVE_VERTICAL = MEDICAL;
